/* EB buy buttons — keeps the WhatsApp order link's message in sync with the
   chosen variant and quantity.

   The link is rendered server-side with the message already built (so it works
   without JS); this only rebuilds the href when the shopper changes the
   quantity or picks another variant. On a variant change Dawn re-fetches the
   section and publishes `variantChange` with that HTML — the fresh, correctly
   money-formatted values are read straight out of it. */

/* Dawn's product-form.js `toggleSubmitButton()` rewrites the button text with its
   own translated "Add to cart" every time a variant change re-enables it, which
   would throw away a merchant's custom label — put it back afterwards. */
if (!customElements.get('eb-buy-buttons')) {
  customElements.define(
    'eb-buy-buttons',
    class EbBuyButtons extends HTMLElement {
      connectedCallback() {
        this.label = this.dataset.atcLabel;
        this.syncAvailability();

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event?.data?.sectionId !== this.dataset.sectionId) return;
            this.restoreLabel();
            this.syncAvailability();
          });
        }
      }

      disconnectedCallback() {
        this.variantUnsubscriber?.();
      }

      get submitButton() {
        return this.querySelector('.eb-buy__btn--atc');
      }

      restoreLabel() {
        const button = this.submitButton;
        // leave sold-out / unavailable wording alone
        if (!this.label || !button || button.hasAttribute('disabled')) return;
        const label = button.querySelector('span');
        if (label) label.textContent = this.label;
      }

      /* Dawn's toggleSubmitButton() is the source of truth for availability; mirror
         it onto the dynamic checkout button, which Shopify leaves clickable even
         when the variant is sold out.

         ⚠ NEVER touch the `.shopify-payment-button__button` element itself. Shopify
         renders it `disabled` and clears that flag once its own click handler is
         wired up; writing `disabled`/`aria-disabled`/`tabindex` onto it races that
         boot sequence and leaves a button that looks enabled but does nothing on
         click (it only came alive after an add-to-cart re-ran Shopify's init).
         Gate our own wrapper instead — `inert` blocks interaction and hides the
         subtree from assistive tech without mutating Shopify's node, and the
         `.eb-buy--unavailable` class carries the styling. */
      syncAvailability() {
        const unavailable = this.submitButton?.hasAttribute('disabled') ?? false;
        this.classList.toggle('eb-buy--unavailable', unavailable);

        // opt-in via the "Disable when sold out" setting; an <a> takes aria-disabled,
        // not disabled, and CSS blocks the pointer
        if (this.classList.contains('eb-buy--wa-follows')) {
          const waLink = this.querySelector('.eb-buy__btn--wa');
          if (waLink) {
            waLink.setAttribute('aria-disabled', String(unavailable));
            waLink.tabIndex = unavailable ? -1 : 0;
          }
        }

        this.querySelector('.eb-buy__dynamic')?.toggleAttribute('inert', unavailable);
      }
    }
  );
}

if (!customElements.get('eb-whatsapp-order')) {
  customElements.define(
    'eb-whatsapp-order',
    class EbWhatsappOrder extends HTMLElement {
      connectedCallback() {
        this.link = this.querySelector('[data-eb-wa]');
        if (!this.link) return;

        this.sectionId = this.dataset.sectionId;
        this.onQuantityChange = this.update.bind(this);
        this.bindQuantity();

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (event?.data?.sectionId !== this.sectionId) return;
            this.syncFromSection(event.data.html);
          });
        }
      }

      disconnectedCallback() {
        this.variantUnsubscriber?.();
        this.unbindQuantity();
      }

      bindQuantity() {
        const id = this.link.dataset.quantityInput;
        this.quantityInput = id ? document.getElementById(id) : null;
        if (!this.quantityInput) return;
        this.quantityInput.addEventListener('change', this.onQuantityChange);
        this.quantityInput.addEventListener('input', this.onQuantityChange);
      }

      unbindQuantity() {
        if (!this.quantityInput) return;
        this.quantityInput.removeEventListener('change', this.onQuantityChange);
        this.quantityInput.removeEventListener('input', this.onQuantityChange);
      }

      /* Copy the freshly rendered variant values from the fetched section HTML. */
      syncFromSection(html) {
        const source = html?.getElementById?.(`EbWhatsApp-${this.sectionId}`);
        if (source) {
          ['variant', 'price', 'priceCents', 'sku', 'url'].forEach((key) => {
            if (source.dataset[key] !== undefined) this.link.dataset[key] = source.dataset[key];
          });
        }
        // the quantity input is replaced on some variant changes
        this.unbindQuantity();
        this.bindQuantity();
        this.update();
      }

      get quantity() {
        const value = parseInt(this.quantityInput?.value, 10);
        return Number.isNaN(value) || value < 1 ? 1 : value;
      }

      update() {
        const data = this.link.dataset;
        if (!data.number || !data.template) return;

        const quantity = this.quantity;
        const cents = parseInt(data.priceCents, 10);
        const total = Number.isNaN(cents)
          ? data.price
          : this.formatMoney(cents * quantity, data.moneyFormat) || data.price;

        const values = {
          '[product]': data.product || '',
          '[variant]': data.variant || '',
          '[price]': data.price || '',
          '[quantity]': String(quantity),
          '[total]': total || '',
          '[sku]': data.sku || '',
          '[url]': data.url || '',
          '[shop]': data.shop || '',
        };

        const substitute = (line) =>
          Object.keys(values).reduce((out, token) => out.split(token).join(values[token]), line);

        // trim + drop empties for parity with the Liquid build: a stray CR or a blank
        // line shows up in WhatsApp as an extra break after every line
        const lines = data.template
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter(Boolean);
        // drop a line whose only content is an empty token (e.g. [variant] on a single-variant
        // product) — unless that empties the message, as with a one-line template
        const kept = lines.filter(
          (line) => !Object.keys(values).some((token) => line.includes(token) && values[token] === '')
        );
        const message = (kept.length ? kept : lines).map(substitute).join('\n').trim();

        this.link.href = `https://wa.me/${data.number}${
          message ? `?text=${encodeURIComponent(message)}` : ''
        }`;
      }

      formatMoney(cents, format) {
        if (!format) return '';
        const placeholder = /\{\{\s*(\w+)\s*\}\}/;
        const match = format.match(placeholder);
        if (!match) return format;

        const formats = {
          amount: [2, ',', '.'],
          amount_no_decimals: [0, ',', '.'],
          amount_with_comma_separator: [2, '.', ','],
          amount_no_decimals_with_comma_separator: [0, '.', ','],
          amount_with_space_separator: [2, ' ', ','],
          amount_no_decimals_with_space_separator: [0, ' ', ','],
          amount_with_apostrophe_separator: [2, "'", '.'],
        };
        const [precision, thousands, decimal] = formats[match[1]] || formats.amount;

        const value = (cents / 100).toFixed(precision);
        const [whole, fraction] = value.split('.');
        const grouped = whole.replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);

        return format.replace(placeholder, fraction ? `${grouped}${decimal}${fraction}` : grouped);
      }
    }
  );
}